import {
  QUIZ_SUBPATH,
  INTRO_SLUG,
  PUP_NAME_SLUG,
  PUP_GENDER_SLUG,
  PUP_BREED_SLUG,
  PUP_STATUS_SLUG,
  CALORIES_SLUG,
  ACTIVITY_SLUG,
  ALLERGIES_SLUG,
  RECOMMENDED_PLAN_SUBPATH,
  ADDONS_SELECTION_SUBPATH,
  CHECKOUT_SUBPATH,
  DELETE_PET_SUBPATH,
  GIFT_WITH_PURCHASE_SUBPATH,
  QUIZ_ABANDONER_MENU_SUBPATH,
  ONBOARDING_INTERSTITIAL_SLUG,
} from '../routing';
import { showPupBreedStep, showGiftOnboardingStep } from './guard';

export const ROOT_SUBPATH = '/onboarding/';
export const SAMPLER_SUBPATH = '/sampler-box';
export const ALL_RECIPES = ['chicken', 'beef', 'lamb', 'turkey', 'pork'];
export const levelToLabel = {
  Low: 'mellow',
  Moderate: 'active',
  High: 'very active',
};

/**
 * Single source of truth for the onboarding milestone steps: their order,
 * route path, and (for conditional steps) the guard that decides whether they
 * appear.
 *
 * Transitional / non-milestone states (interstitial, submitCart, deletePet,
 * quizAbandonerMenu, the initial bootstrap) are added to STATE below.
 */
export const STEPS = [
  { key: 'intro', path: `${INTRO_SLUG}` },
  { key: 'pupName', path: `${QUIZ_SUBPATH}/${PUP_NAME_SLUG}` },
  { key: 'pupGender', path: `${QUIZ_SUBPATH}/${PUP_GENDER_SLUG}` },
  {
    key: 'pupBreed',
    path: `${QUIZ_SUBPATH}/${PUP_BREED_SLUG}`,
    isActive: showPupBreedStep,
  },
  { key: 'pupStatus', path: `${QUIZ_SUBPATH}/${PUP_STATUS_SLUG}` },
  { key: 'weight', path: `${QUIZ_SUBPATH}/${CALORIES_SLUG}` },
  { key: 'activity', path: `${QUIZ_SUBPATH}/${ACTIVITY_SLUG}` },
  { key: 'allergies', path: `${QUIZ_SUBPATH}/${ALLERGIES_SLUG}` },
  { key: 'recommendedPlan', path: RECOMMENDED_PLAN_SUBPATH },
  {
    key: 'giftWithPurchase',
    path: GIFT_WITH_PURCHASE_SUBPATH,
    isActive: showGiftOnboardingStep,
  },
  { key: 'extras', path: ADDONS_SELECTION_SUBPATH },
  { key: 'checkout', path: CHECKOUT_SUBPATH },
];

export const STEP_KEYS = STEPS.map(step => step.key);
export const STEP_PATHS = STEPS.map(step => step.path);

export const STATE = {
  initial: ROOT_SUBPATH,
  initialSampler: SAMPLER_SUBPATH,
  interstitial: `${QUIZ_SUBPATH}/${ONBOARDING_INTERSTITIAL_SLUG}`,
  submitCart: 'submit',
  deletePet: DELETE_PET_SUBPATH,
  quizAbandonerMenu: QUIZ_ABANDONER_MENU_SUBPATH,
  ...Object.fromEntries(STEPS.map(step => [step.key, step.path])),
};

// Ordered onboarding milestone keys.
export const MILESTONES = STEP_KEYS;

export const QUIZ_MILESTONES = MILESTONES.filter(key =>
  STATE[key]?.startsWith(`${QUIZ_SUBPATH}/`),
);

export const ACTION_TYPES = {
  initialLoad: 'initialLoad',
  toIntro: 'toIntro',
  toQuizAbandonerMenu: 'toQuizAbandonerMenu',
  toPupName: 'toPupName',
  toPupGender: 'toPupGender',
  toPupBreed: 'toPupBreed',
  toPupStatus: 'toPupStatus',
  toWeight: 'toWeight',
  toActivity: 'toActivity',
  toAllergies: 'toAllergies',
  toInterstitial: 'toInterstitial',
  continueFromInterstitial: 'continueFromInterstitial',
  toRecommendedPlan: 'toRecommendedPlan',
  toGiftWithPurchase: 'toGiftWithPurchase',
  toExtras: 'toExtras',
  toCheckout: 'toCheckout',
  selectedFreshRecipes: 'selectedFreshRecipes',
  selectedMixedRecipes: 'selectedMixedRecipes',
  goBack: 'goBack',
  deletePet: 'deletePet',
};

export const ACTIONS = {
  initialLoad: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.initialLoad,
    };
  },
  toIntro: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toIntro,
    };
  },
  toQuizAbandonerMenu: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toQuizAbandonerMenu,
    };
  },
  toPupName: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toPupName,
    };
  },
  toPupGender: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toPupGender,
    };
  },
  toPupBreed: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toPupBreed,
    };
  },
  toPupStatus: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toPupStatus,
    };
  },
  toWeight: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toWeight,
    };
  },
  toActivity: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toActivity,
    };
  },
  toAllergies: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toAllergies,
    };
  },
  toInterstitial: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toInterstitial,
    };
  },
  continueFromInterstitial: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.continueFromInterstitial,
    };
  },
  toRecommendedPlan: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toRecommendedPlan,
    };
  },

  toGiftWithPurchase: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toGiftWithPurchase,
    };
  },
  toExtras: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toExtras,
    };
  },
  toCheckout: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.toCheckout,
    };
  },
  selectedFreshRecipes: (payload = {}, props) => {
    return {
      payload,
      type: ACTION_TYPES.selectedFreshRecipes,
      props,
    };
  },
  selectedMixedRecipes: (payload = {}, props) => {
    return {
      payload,
      type: ACTION_TYPES.selectedMixedRecipes,
      props,
    };
  },
  goBack: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.goBack,
    };
  },
  deletePet: (payload = {}) => {
    return {
      payload,
      type: ACTION_TYPES.deletePet,
    };
  },
};

const ROUTED_STATE_KEYS = [...STEP_KEYS, 'quizAbandonerMenu'];

//* This is a mapping of state paths to action creators. It is used to determine the action to take when a user navigates to a given state.
//* Must be kept in sync with ROUTED_STATE_KEYS and it will always begin with 'to' followed by the state key with the first letter capitalized.
export const pathToAction = Object.fromEntries(
  ROUTED_STATE_KEYS.map(stateKey => [
    STATE[stateKey],
    ACTIONS[`to${stateKey[0].toUpperCase()}${stateKey.slice(1)}`],
  ]),
);

export const unmappedStates = [
  STATE.initial,
  STATE.initialSampler,
  STATE.submitCart,
];
